/**
 * Extensions API - Serves extension data for IDE integration
 * This file provides functions to export/import extension data
 */

import { useExtensionsStore, type Extension, type ExtensionCategory } from '@/services/extensionsService';

// API Response types
export interface ExtensionAPIResponse {
  success: boolean;
  data: MarketplaceExtensionAPI[];
  total: number;
  timestamp: string;
}

export interface MarketplaceExtensionAPI {
  id: string;
  name: string;
  displayName: string;
  description: string;
  version: string;
  publisher: string;
  iconUrl?: string;
  downloadUrl: string;
  homepageUrl?: string;
  repositoryUrl?: string;
  categories: string[];
  tags: string[];
  downloads: number;
  rating: number;
  ratingCount: number;
  lastUpdated: number;
  isVerified: boolean;
  changelog?: string;
}

// Convert admin extension to marketplace API format
export function extensionToAPI(ext: Extension): MarketplaceExtensionAPI {
  // Map admin categories to IDE categories
  const categoryMap: Record<ExtensionCategory, string[]> = {
    Languages: ['Language'],
    Themes: ['Theme'],
    SCM: ['Git'],
    AI: ['AI'],
    DevOps: ['Remote', 'Productivity'],
    Database: ['Data'],
    Snippets: ['Snippet'],
    Debuggers: ['Debugger'],
    Formatters: ['Formatter'],
    Other: ['Other'],
  };

  return {
    id: ext.id,
    name: ext.name.toLowerCase().replace(/\s+/g, '-'),
    displayName: ext.name,
    description: ext.description,
    version: ext.version,
    publisher: ext.publisher,
    iconUrl: ext.icon,
    downloadUrl: `https://ochtopus.com/extensions/${ext.id}/download`,
    homepageUrl: ext.homepage,
    repositoryUrl: ext.repositoryUrl,
    categories: categoryMap[ext.category] || ['Other'],
    tags: ext.tags,
    downloads: ext.downloads,
    rating: ext.rating,
    ratingCount: ext.ratingCount,
    lastUpdated: new Date(ext.submittedAt).getTime() / 1000,
    isVerified: ext.publisher === 'Ochtopus',
    changelog: ext.changelog,
  };
}

// Get all approved extensions as API response
export function getExtensionsAPIResponse(): ExtensionAPIResponse {
  const { extensions } = useExtensionsStore.getState();
  const approvedExtensions = extensions.filter(ext => ext.status === 'approved');

  return {
    success: true,
    data: approvedExtensions.map(extensionToAPI),
    total: approvedExtensions.length,
    timestamp: new Date().toISOString(),
  };
}

// Export extensions to JSON string
export function exportExtensionsJSON(): string {
  const response = getExtensionsAPIResponse();
  return JSON.stringify(response, null, 2);
}

// Generate static extensions data file
export function generateExtensionsData(): MarketplaceExtensionAPI[] {
  const { extensions } = useExtensionsStore.getState();
  return extensions
    .filter(ext => ext.status === 'approved')
    .map(extensionToAPI);
}
